import React, { useState, useEffect, useCallback, useRef } from 'react';
import { webOrigin } from '../utils/brand';
import { friendService } from '../services';
import inviteService from '../services/inviteService';
import { Sheet, PillButton, Face, inputStyle, MUTED, SUBTLE, RULE, RED } from './club/clubUi';

const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isMobileUa = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * The share / invite sheet (README §1 "Share / invite"): `INVITE TO THE CLUB` (members) /
 * `SHARE THIS CLUB`; the link + COPY → cyan COPIED; MESSAGES · EMAIL · MORE… (navigator.share);
 * members get INVITE FRIENDS — a search over people on Portal plus the invitable friends list,
 * each row INVITE → INVITED ✓ (cyan) / IN THE CLUB (grey). `clubService` is injected so the
 * page's mocks reach it.
 *
 * The invite link is the club's `heyportal.app/i/{code}` (Oct 2026): the same "through the
 * portal" landing a person's invite card gets, showing the club's logo and about, then the
 * join screen. It is minted on the first open; until it lands the sheet shows the bare
 * `/clubs/invite/{code}` link, which still works.
 */
export default function ClubShareSheet({ isOpen, onClose, club, clubService, mobile = false }) {
  const [copied, setCopied] = useState(false);
  const [invitableFriends, setInvitableFriends] = useState([]);
  const [inviteFriendsLoading, setInviteFriendsLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState({});
  const [inviteError, setInviteError] = useState({});
  const [hasFetchedFriends, setHasFetchedFriends] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);
  const [inviteLink, setInviteLink] = useState(null);

  const canInvite = !!(club?.id && clubService && club.inviteCode && club.userRole);
  const shareUrl = club ? (canInvite ? (inviteLink || `${webOrigin()}/clubs/invite/${club.inviteCode}`) : `${webOrigin()}/clubs/${club.slug || club.id}`) : '';

  useEffect(() => {
    if (!isOpen || !canInvite || inviteLink) return undefined;
    let live = true;
    inviteService.forClub(club.id).then((r) => {
      if (live && r.success && r.data?.url) setInviteLink(r.data.url);
    });
    return () => { live = false; };
  }, [isOpen, canInvite, inviteLink, club?.id]);
  useEffect(() => { setInviteLink(null); }, [club?.id]);
  const shareText = club ? `Join ${club.name} on Portal! ${shareUrl}` : '';

  const fetchInvitableFriends = useCallback(async () => {
    if (!club?.id || !clubService) return;
    setInviteFriendsLoading(true);
    try {
      const result = await clubService.getInvitableFriends(club.id);
      if (result.success) setInvitableFriends(result.data || []);
    } catch (err) {
      console.error('Failed to fetch invitable friends:', err);
    } finally {
      setInviteFriendsLoading(false);
      setHasFetchedFriends(true);
    }
  }, [club?.id, clubService]);

  useEffect(() => { if (isOpen && canInvite && !hasFetchedFriends) fetchInvitableFriends(); }, [isOpen, canInvite, hasFetchedFriends, fetchInvitableFriends]);
  useEffect(() => { if (!isOpen) { setHasFetchedFriends(false); setSearchQuery(''); setSearchResults([]); setCopied(false); } }, [isOpen]);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return undefined; }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await friendService.searchPeople(searchQuery, 10);
        if (result.success) setSearchResults(result.data || []);
      } catch (err) {
        console.error('Failed to search people:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchQuery]);

  const handleInviteFriend = async (userId) => {
    if (!club?.id || !clubService) return;
    setInviteLoading((prev) => ({ ...prev, [userId]: true }));
    setInviteError((prev) => ({ ...prev, [userId]: null }));
    const result = await clubService.inviteFriend(club.id, userId);
    if (result.success) {
      setInvitableFriends((prev) => prev.map((f) => (f.userId === userId ? { ...f, status: 'invited' } : f)));
      setSearchResults((prev) => prev.map((p) => (p.userId === userId ? { ...p, status: 'invited' } : p)));
    } else setInviteError((prev) => ({ ...prev, [userId]: result.error || 'Failed to invite' }));
    setInviteLoading((prev) => ({ ...prev, [userId]: false }));
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch (err) { console.error('Failed to copy:', err); }
  };
  const viaMessages = async () => {
    if (isMobileUa) { window.location.href = isIOS ? `sms:&body=${encodeURIComponent(shareText)}` : `sms:?body=${encodeURIComponent(shareText)}`; return; }
    await copy();
  };
  const viaEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent(`Join ${club?.name} on Portal`)}&body=${encodeURIComponent(shareText)}`; };
  const viaMore = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: club?.name, text: shareText, url: shareUrl }); return; }
      await copy();
    } catch { /* dismissed */ }
  };

  const row = (person, statusOverride, i) => {
    const friendMatch = invitableFriends.find((f) => f.userId === person.userId);
    const status = statusOverride || friendMatch?.status || (person.status === 'invited' ? 'invited' : 'none');
    const inClub = status === 'applied' || status === 'member';
    const invited = status === 'invited';
    const busy = !!inviteLoading[person.userId];
    return (
      <span key={person.userId} className="flex items-center" style={{ gap: 12, padding: '10px 0', borderTop: i ? RULE : 'none' }} data-testid={`invite-row-${person.userId}`}>
        <Face src={person.avatarUrl} name={person.displayName} size={40} tintKey={person.userId} />
        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
          <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{person.displayName}</span>
          {(person.handle || inviteError[person.userId]) && <span className="truncate" style={{ fontSize: 11, color: inviteError[person.userId] ? RED : MUTED }}>{inviteError[person.userId] || `@${person.handle}`}</span>}
        </span>
        <PillButton variant={inClub ? 'tertiary' : invited ? 'cyan' : 'primary'} size={34} fontSize={9} disabled={inClub || invited || busy} style={inClub ? { color: SUBTLE, cursor: 'default' } : invited ? { cursor: 'default' } : undefined} onClick={() => !inClub && !invited && handleInviteFriend(person.userId)} data-testid={`invite-${person.userId}`}>
          {busy ? '…' : inClub ? 'IN THE CLUB' : invited ? 'INVITED ✓' : 'INVITE'}
        </PillButton>
      </span>
    );
  };

  if (!isOpen) return null;
  return (
    <Sheet onClose={onClose} phone={mobile} width={520} title={canInvite ? 'INVITE TO THE CLUB' : 'SHARE THIS CLUB'} testId="share-sheet">
      <span className="flex items-center" style={{ gap: 6, padding: '6px 6px 6px 18px', borderRadius: 999, background: '#F5F5F5' }}>
        <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12 }} data-testid="share-url">{shareUrl.replace(/^https?:\/\//, '')}</span>
        <PillButton variant={copied ? 'cyan' : 'primary'} size={38} fontSize={10} onClick={copy} data-testid="share-copy">{copied ? 'COPIED' : 'COPY'}</PillButton>
      </span>
      <span className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
        <PillButton variant="tertiary" size={44} fontSize={10} weight={700} tracking=".12em" onClick={viaMessages}>MESSAGES</PillButton>
        <PillButton variant="tertiary" size={44} fontSize={10} weight={700} tracking=".12em" onClick={viaEmail}>EMAIL</PillButton>
        <PillButton variant="tertiary" size={44} fontSize={10} weight={700} tracking=".12em" onClick={viaMore}>MORE…</PillButton>
      </span>
      {canInvite && (
        <span className="flex flex-col" style={{ gap: 10 }} data-testid="invite-friends">
          <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>INVITE FRIENDS</span>
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search people" aria-label="Search people" style={{ ...inputStyle, height: 46 }} data-testid="invite-search" />
          <span className="flex flex-col">
            {searchQuery.trim() ? (
              searchLoading ? <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE, padding: '10px 0' }}>SEARCHING…</span>
                : searchResults.length === 0 ? <span style={{ fontSize: 12, color: MUTED, padding: '10px 0' }}>No one by that name.</span>
                  : searchResults.map((p, i) => row(p, null, i))
            ) : (
              inviteFriendsLoading ? <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE, padding: '10px 0' }}>LOADING…</span>
                : invitableFriends.length === 0 ? <span style={{ fontSize: 12, color: MUTED, padding: '10px 0' }}>Add friends to invite them to your club. Search above for anyone on Portal.</span>
                  : invitableFriends.map((f, i) => row(f, f.status, i))
            )}
          </span>
        </span>
      )}
    </Sheet>
  );
}
